import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Building2, ChevronRight, HardHat } from 'lucide-react';
import LanguageSelector from '../components/LanguageSelector';
import { useLanguage } from '../context/LanguageContext';
import { exitToMarketingSite } from '../utils/marketingSiteExit';
import { cn } from '@/lib/utils';

export default function PortalGatewayPage() {
  const { t } = useLanguage();

  const handleBack = () => {
    exitToMarketingSite();
  };

  const portals = [
    {
      id: 'client',
      title: t('portalClient'),
      sub: t('portalClientSub'),
      icon: HardHat,
      loginTo: '/login/client',
      registerTo: '/register/client',
      registerLabel: t('portalNewClientRegister'),
      glow: 'hover:shadow-[0_12px_40px_rgba(201,168,76,0.12)]',
    },
    {
      id: 'owner',
      title: t('portalOwner'),
      sub: t('portalOwnerSub'),
      icon: Building2,
      loginTo: '/login/owner',
      registerTo: '/register/owner',
      registerLabel: t('portalNewOwnerRegister'),
      glow: 'hover:shadow-[0_12px_40px_rgba(201,168,76,0.12)]',
    },
  ];

  return (
    <div className="relative min-h-dvh bg-[#050d1a] font-sans text-white">
      <div
        className="pointer-events-none fixed inset-0 opacity-60"
        style={{
          background:
            'radial-gradient(ellipse 80% 50% at 50% -10%, rgba(201,168,76,0.12), transparent), radial-gradient(ellipse 60% 40% at 100% 100%, rgba(15,32,64,0.9), transparent)',
        }}
      />

      <div className="relative z-10 mx-auto flex min-h-dvh max-w-md flex-col px-5 pb-8 pt-[calc(env(safe-area-inset-top,0px)+24px)]">
        <header className="mb-10 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={handleBack}
            className={cn(
              'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl',
              'border border-white/10 bg-white/[0.04] text-white/55',
              'transition-colors hover:border-[#C9A84C]/35 hover:bg-white/[0.08] hover:text-[#C9A84C]',
            )}
            aria-label={t('back')}
          >
            <ArrowLeft className="h-5 w-5" strokeWidth={2} />
          </button>
          <div className="flex min-w-0 flex-1 items-center justify-center gap-2.5">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#C9A84C] text-xs font-black text-[#0a1628]">
              DE
            </div>
            <span className="truncate text-sm font-semibold text-white/90">{t('appName')}</span>
          </div>
          <LanguageSelector compact iconOnly />
        </header>

        <div className="mb-8 text-center">
          <h1 className="text-[1.65rem] font-bold tracking-tight text-white">{t('portalWelcome')}</h1>
          <p className="mt-2 text-[12px] leading-snug text-white/55">{t('portalWelcomeHint')}</p>
        </div>

        <div className="flex flex-1 flex-col justify-center gap-4">
          {portals.map((portal) => {
            const Icon = portal.icon;
            return (
              <Link
                key={portal.id}
                to={portal.loginTo}
                replace
                className={cn(
                  'group flex items-center gap-4 rounded-2xl border border-white/[0.08] bg-white/[0.04] p-5',
                  'transition-all duration-200 hover:border-[#C9A84C]/35 hover:bg-white/[0.06]',
                  portal.glow,
                )}
              >
                <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-[#C9A84C]/12 text-[#E8D5A3] transition-colors group-hover:bg-[#C9A84C]/20">
                  <Icon className="h-7 w-7" strokeWidth={1.5} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-lg font-bold text-white">{portal.title}</p>
                  <p className="text-[12px] leading-snug text-white/55">{portal.sub}</p>
                </div>
                <ChevronRight className="h-5 w-5 shrink-0 text-[#C9A84C]/70 transition-transform group-hover:translate-x-0.5" />
              </Link>
            );
          })}
        </div>

        <div className="mt-10 space-y-4">
          <div className="flex justify-center gap-6 text-[12px] leading-snug text-white/50">
            {portals.map((p) => (
              <Link key={p.id} to={p.registerTo} className="hover:text-[#C9A84C]">
                {p.registerLabel}
              </Link>
            ))}
          </div>
          <div className="flex flex-wrap justify-center gap-x-3 gap-y-1 border-t border-white/[0.06] pt-4 text-[10px] text-white/40">
            <Link to="/terms" className="hover:text-[#C9A84C]">Terms</Link>
            <Link to="/privacy" className="hover:text-[#C9A84C]">Privacy</Link>
            <Link to="/delete-account" className="hover:text-[#C9A84C]">Delete account</Link>
            <Link to="/contact" className="hover:text-[#C9A84C]">Contact</Link>
            <Link to="/service-delivery-policy" className="hover:text-[#C9A84C]">Delivery</Link>
            <Link to="/cancellation-and-refunds" className="hover:text-[#C9A84C]">Refunds</Link>
            <Link to="/pricing" className="hover:text-[#C9A84C]">Pricing</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
